import { SCREEN_PATHS } from '@/shared/routes';

/* The information architecture, as data.
 *
 * **Seven groups replacing three, and the change is a regrouping rather than a rename.** The previous
 * shell had Catalog / Quality / Platform, and Quality had grown to seventeen entries — which is the
 * flat list the redesign exists to remove. Seventeen items under one heading is not navigation; it is
 * a list somebody reads top to bottom every time.
 *
 * The grouping principle is **what a reader is trying to do**, not which module owns the code. That is
 * why remediation, bulk changes, what-if and autonomous are together under Action even though they
 * come from four different backend modules: all four *change something*, and a steward looking for
 * "how do I fix this" should find them in one place. Equally, contracts and residency sit under
 * Governance rather than Quality — a contract is a promise to a counterparty and residency is a legal
 * constraint, and neither measures data.
 *
 * Every entry resolves to a route that exists in `SCREEN_PATHS`; `navigation.test.ts` asserts the two
 * agree in both directions, so a route added without a nav entry fails the build rather than becoming
 * unreachable, and an entry pointing at a deleted route fails too. That check is the reason this file
 * is data rather than JSX.
 *
 * Four judgements worth not reversing:
 *
 * * **Lineage is under Data, not Quality.** It answers "what is this connected to", which is a
 *   question about the catalogue's contents. Grouping it with rules and scores would suggest it
 *   measures something, and it measures nothing.
 * * **PII review is under Data.** It is a queue about *columns*, and a steward reaches it from the
 *   catalogue rather than from a quality score.
 * * **Alert routing is under Quality, beside incidents.** It answers the question the incident queue
 *   raises — an incident nobody was told about looks identical to one nobody had to be told about.
 * * **Model routing is under AI, not Platform.** It was under Platform when it shipped because it
 *   reads as deployment configuration; it is the decision every AI feature depends on, and an
 *   administrator looking for it is thinking about the AI plane.
 */

export interface NavEntry {
  readonly to: string;
  readonly labelKey: string;
}

export interface NavGroup {
  /** Stable id, used as the persistence key for the expanded/collapsed state. */
  readonly id: string;
  readonly labelKey: string;
  readonly entries: readonly NavEntry[];
}

export const NAV_GROUPS: readonly NavGroup[] = [
  /* **Home is a group of one, not a bare link above the groups.** It was the second, and it made the
   * top of the sidebar the only place where a row sat with no heading over it — so the eye had to
   * learn two layouts before reaching Connections. A group of one costs a heading and buys a single
   * rhythm down the whole rail. */
  {
    id: 'home',
    labelKey: 'nav.section.home',
    entries: [{ to: SCREEN_PATHS.home, labelKey: 'nav.home' }],
  },
  {
    id: 'data',
    labelKey: 'nav.section.data',
    entries: [
      { to: SCREEN_PATHS.connections, labelKey: 'nav.connections' },
      { to: SCREEN_PATHS.datasets, labelKey: 'nav.datasets' },
      { to: SCREEN_PATHS.lineage, labelKey: 'nav.lineage' },
      { to: SCREEN_PATHS.review, labelKey: 'nav.review' },
    ],
  },
  {
    id: 'quality',
    labelKey: 'nav.section.quality',
    entries: [
      { to: SCREEN_PATHS.rules, labelKey: 'nav.rules' },
      { to: SCREEN_PATHS.executions, labelKey: 'nav.executions' },
      { to: SCREEN_PATHS.scores, labelKey: 'nav.scores' },
      /* Anomalies before incidents: an anomaly is what an incident is opened *from*, so the reading
       * order is cause then consequence. */
      { to: SCREEN_PATHS.anomalies, labelKey: 'nav.anomalies' },
      { to: SCREEN_PATHS.incidents, labelKey: 'nav.incidents' },
      { to: SCREEN_PATHS.alertRouting, labelKey: 'nav.alertRouting' },
      { to: SCREEN_PATHS.reconciliation, labelKey: 'nav.reconciliation' },
    ],
  },
  {
    id: 'governance',
    labelKey: 'nav.section.governance',
    entries: [
      { to: SCREEN_PATHS.policy, labelKey: 'nav.policy' },
      { to: SCREEN_PATHS.compliance, labelKey: 'nav.compliance' },
      { to: SCREEN_PATHS.contracts, labelKey: 'nav.contracts' },
    ],
  },
  {
    id: 'action',
    labelKey: 'nav.section.action',
    entries: [
      { to: SCREEN_PATHS.remediation, labelKey: 'nav.remediation' },
      { to: SCREEN_PATHS.bulk, labelKey: 'nav.bulk' },
      { to: SCREEN_PATHS.autonomous, labelKey: 'nav.autonomous' },
    ],
  },
  {
    id: 'ai',
    labelKey: 'nav.section.ai',
    entries: [
      { to: SCREEN_PATHS.copilot, labelKey: 'nav.copilot' },
      { to: SCREEN_PATHS.suggestions, labelKey: 'nav.suggestions' },
      { to: SCREEN_PATHS.evals, labelKey: 'nav.evals' },
      { to: SCREEN_PATHS.serving, labelKey: 'nav.serving' },
    ],
  },
  {
    id: 'platform',
    labelKey: 'nav.section.platform',
    entries: [
      { to: SCREEN_PATHS.marketplace, labelKey: 'nav.marketplace' },
      { to: SCREEN_PATHS.audit, labelKey: 'nav.audit' },
    ],
  },
  /* **Administration is its own group at the bottom, not scattered through the other six.**
   * `Team` used to sit in Platform; access policy is in Governance, model routing in AI, quotas in
   * Platform - so an administrator setting up a tenant had no single place to go. This group is the
   * front door, and the existing screens keep their own routes: `/admin` links to them rather
   * than duplicating them as tabs, which would give one screen two routes.
   *
   * **That argument is now applied to Connectors and Score model too, which used to sit here as
   * tabs beside the front door that already linked to them.** Both were the two-doors shape the
   * comment above rejects, and removing the tabs leaves the door `/admin` already provides. */
  {
    id: 'admin',
    labelKey: 'nav.section.admin',
    entries: [{ to: SCREEN_PATHS.admin, labelKey: 'nav.admin' }],
  },
];

/** Groups that are deliberately one entry long, each with the argument that admits it.
 *
 * **A registry rather than a loosened bound**, for the reason `NOT_IN_NAV` and
 * `PLATFORM_SCOPED_TABLES` are registries: "a group of one is not worth a heading" is a good rule
 * and this is a real exception to it, so the exception is written down where a reviewer sees it
 * beside the next one. Dropping the assertion instead would let the *accidental* single-entry group
 * — a section somebody split and never filled — back in silently.
 */
export const SINGLE_ENTRY_GROUPS: Readonly<Record<string, string>> = {
  admin:
    'Connectors and Score model were tabs here beside the `/admin` front door that already links ' +
    'to both, which is the two-doors-for-one-screen shape this group’s own comment rejects. With ' +
    'the tabs gone the heading stands over one entry, and it earns the line: the group is where an ' +
    'administrator looks, it is the only one hidden from a non-admin by `ADMIN_ONLY_GROUPS`, and ' +
    'folding `Users & access` into Platform would put the admin front door under a heading a ' +
    'non-admin can see.',
  home:
    'The landing page. It was an entry above the groups and became a group of one so the rail has ' +
    'one rhythm from top to bottom — a reader should not have to learn two layouts before reaching ' +
    'Connections. The heading costs a line and buys that.',
};

/* Which groups start open. All of them, and that is deliberate for a first visit: a reader who has
 * never seen the product should be able to see its whole shape at once, and the collapse is there for
 * somebody who already knows where they are going. The choice is then remembered per group. */
export const DEFAULT_EXPANDED: readonly string[] = NAV_GROUPS.map((group) => group.id);

export const NAV_STORAGE_KEY = 'lumiq.nav.expanded';

/* Which groups this browser has *seen*, so a group added in a later release can be told apart from
 * one the user deliberately collapsed. Without it the expanded set is ambiguous about absence, and a
 * new group ships collapsed for everybody who has used the app before. */
export const NAV_SEEN_KEY = 'lumiq.nav.seen';
export const NAV_COLLAPSED_KEY = 'lumiq.nav.collapsed';
